import * as React from 'react';
import {
  useProfilePhoto,
  UseProfilePhotoProps,
} from '@/components/ui/hooks/useProfilePhoto';
import { Camera, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
// import { Button } from './button';
// import { Alert, AlertDescription } from './alert';
// import ErrorMessage from '../ErrorMessage';

export interface ProfilePhotoInputProps
  extends Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      'value' | 'onChange'
    >,
    UseProfilePhotoProps {
  containerClassName?: string;
}

const ProfilePhotoInput = React.forwardRef<
  HTMLInputElement,
  ProfilePhotoInputProps
>(
  (
    { className, containerClassName, onChange, value, maxSize, ...props },
    forwardedRef
  ) => {
    const {
      imagePreview,
      getRootProps,
      getInputProps,
      isDragActive,
      // removeImage,
      // error,
    } = useProfilePhoto({ maxSize, onChange, value });
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(forwardedRef, () => inputRef.current!, []);

    const handleClick = () => {
      inputRef.current?.click();
    };

    return (
      <div className={cn('flex flex-col items-center', containerClassName)}>
        <div className="group relative">
          <div
            {...getRootProps()}
            className={cn(
              'relative h-28 w-28 cursor-pointer overflow-hidden rounded-full ring-2 ring-offset-2 transition-shadow',
              isDragActive
                ? 'ring-primary ring-offset-gray-100'
                : 'ring-gray-200',
              className
            )}
          >
            <input
              {...getInputProps()}
              ref={inputRef}
              aria-label="Upload profile photo"
              {...props}
            />
            <Avatar className="h-full w-full">
              <AvatarImage
                src={imagePreview || undefined}
                alt="Profile preview"
                className="object-cover"
              />
              <AvatarFallback className="bg-gradient-to-br from-[#3a3a3a] to-primary">
                <User className="h-1/2 w-1/2 text-white/85" />
              </AvatarFallback>
            </Avatar>
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/45 opacity-0 backdrop-blur-[1px] transition-opacity group-hover:opacity-100"
              onClick={handleClick}
            >
              <Camera className="h-6 w-6 text-white" />
              <span className="text-[10px] font-medium uppercase tracking-wide text-white">
                {imagePreview ? 'Change' : 'Upload'}
              </span>
            </div>
          </div>

          {/* Camera badge — signals the avatar is editable */}
          <button
            type="button"
            onClick={handleClick}
            aria-label="Upload profile photo"
            className="absolute bottom-0.5 right-0.5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-primary text-white shadow-md transition-colors hover:bg-primary/85"
          >
            <Camera className="h-4 w-4" />
          </button>
        </div>
        {/* {error && (
          <div className="mt-4 max-w-md">
            <ErrorMessage error={error} />
          </div>
        )} */}
        {/* Uncomment to enable photo upload */}
      </div>
    );
  }
);
ProfilePhotoInput.displayName = 'ProfilePhotoInput';

export { ProfilePhotoInput };
